import { useCallback, useMemo, useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  DashButton,
  DataSurface,
  /* ⚠️ ALIASED — this file already has a `Field`, and it is a different thing: a
     read-only `<dt>/<dd>` pair for a child's facts. The kit's is a labelled
     control wrapper. Renaming the local one would touch D5's whole fact list
     for no gain. */
  Field as FormField,
  GamesPanel,
  InfoIcon,
  PointIcon,
  PrivacyIcon,
  Select,
  StatusPill,
  StickerIcon,
  TrendUpIcon,
  /* ⚠️ FROM `shared/components`, NOT `./TeacherUI` — that shim says new work
     imports from the kit directly, and it carries neither of these. */
  RewardTile,
  StatGrid,
  ActivityMeter,
  LevelMeter,
  /* The de-emoji set: initials instead of 👦, and stroke icons for the four
     section headings that opened with 💡 📅 👥 ⚙️. */
  Avatar,
  CalendarIcon,
  ChildrenIcon,
  RegisterIcon,
  SparkleIcon,
} from '../../../shared/components'
import { LEVEL_COUNT } from '../../../shared/curriculum/progress'
import { useResource, type Resource } from '../../../shared/hooks/useResource'
import {
  getInsight,
  getPlayerEvents,
  getRoster,
  type EventRow,
  type Insight,
  type RosterRow,
  type StaffAuth,
} from '../../../shared/api/teaching'
import {
  getPlayerProgress,
  getPlayerSessions,
  markAbsentToday,
  reassignSession,
  setPlayerBand,
  unmarkAbsentToday,
  type ProgressSnapshot,
  type SessionRow,
} from '../../../shared/api/student'
import { getPlayerGames, type GameStatsResponse } from '../../../shared/api/games'
import { GAMES_PANEL_COPY } from '../../../shared/ui/gamesCopy'
import type { ClassSummary } from '../../../shared/api/classes'
import type { AgeBand } from '../../../shared/types/prisma-enums'
import { TEACHER_COPY, describeError, type Lang } from './copy'
import { BAND_LABEL, SKILL_LABEL, STUDENT_COPY } from './studentCopy'
import { classLabel } from './classChoice'
import { Panel, TableCard, Td, Thead, Tr } from './TeacherUI'
import { StrengthsPanel } from './StrengthsPanel'
import { EditChildPanel, MoveClassPanel, RemoveFromClassPanel } from './RosterControls'

/**
 * D5 — one child, everything the product truthfully knows, and the four teacher
 * controls that had no UI.
 *
 * 🔴 WHAT THIS SCREEN CANNOT SHOW, AND WHY THAT IS THE DESIGN. A `Player` row
 * holds a **name, an avatar, an age band and a roll number**. There is no date
 * of birth, address, photograph, gender or note — ADR-004 keeps a child's
 * record minimal on purpose, and `scripts/check-no-free-text.mjs` fails the
 * build if anyone adds a notes column. So "complete bio data" has a deliberate
 * ceiling, and the page states it (`noOtherData`) rather than leaving a teacher
 * to conclude the page half-failed to load.
 *
 * 🔴 THE CONTROLS BELOW CAME FROM A CONTROLLER NOTHING CALLED.
 * `teaching/controls.controller.ts` is `@Roles('TEACHER','ADMIN')`, built,
 * scoped and tested, with no screen on the other end — T1 reassign, T4 band and
 * T7 absent. `information-architecture.md` §7 files T1 under
 * *"correctness-or-rights gaps rather than features"*.
 *
 * ✅ ITS FOURTH ROUTE, `PATCH /players/:id/class`, IS OFFERED NOW — AND SO ARE
 * TWO THAT DID NOT EXIST. This file used to carry a note saying the move control
 * *"is deliberately not offered here"* because it needed class WRITE scope,
 * *"and no admin can reach this screen"*. That was an accurate description of a
 * dead end: S5's *"mid-year reshuffles happen constantly"* had a built, scoped,
 * tested endpoint and no user at all. `ScopeService` has a `roster` level now —
 * between "look" and "import a spreadsheet of parent PII" — so the register is
 * the teacher's, and adding a child and taking one off the roll come with it.
 * See `RosterControls`.
 *
 * ✅ AND "STRENGTHS" MEANS SOMETHING NOW. The insight panel below ranks skills
 * by EVENT COUNT and prints the top two under that word, so a child who spent
 * the week failing Letter Lagoon was described to their teacher as strong at
 * literacy. It stays — it is a fair summary of where the time went — and
 * `StrengthsPanel` answers the question the teacher opened the page for, from
 * the answers themselves.
 *
 * ⚠️ EVERY PANEL ISSUES ITS OWN REQUEST AND HANDLES ITS OWN FOUR STATES. No
 * fixture, no prop-drilled rows — §8.4's discipline.
 */

export interface StudentPageProps {
  playerId: string
  classId: string
  lang?: Lang
  auth?: StaffAuth
  /** Every class this teacher may move the child into. */
  classes: readonly ClassSummary[]
  /** Told when a write lands, so the class-level screens refetch. */
  onChanged?: () => void
  /**
   * 🔴 IT WAS DESTRUCTURED, PASSED AND NEVER DECLARED. `TeacherApp` hands this
   * in and the component wires it to the back control at the top of the page, so
   * the button works — but neither end typechecked, which meant nothing would
   * have caught the prop being dropped in a refactor. On this screen that leaves
   * a teacher on one child's page with no way back to the roster except the
   * browser's own control, mid-lesson.
   */
  onBack?: () => void
}

export function StudentPage({
  playerId,
  classId,
  lang = 'en',
  auth,
  classes,
  onBack,
  onChanged,
}: StudentPageProps) {
  const t = TEACHER_COPY[lang]
  const s = STUDENT_COPY[lang]
  /* ⚠️ CLOSED BY DEFAULT. The page's job is reading a child; the correction is
     the rarer act, and a form open on arrival puts two editable boxes above the
     progress a teacher opened the page for. */
  const [editing, setEditing] = useState(false)

  const roster = useResource<RosterRow[]>(
    (signal) => getRoster(classId, { ...auth, signal }),
    ['student-roster', classId],
  )
  const progress = useResource<ProgressSnapshot>(
    (signal) => getPlayerProgress(playerId, { ...auth, signal }),
    ['student-progress', playerId],
  )
  const insight = useResource<Insight>(
    (signal) => getPlayerEvents(playerId, { ...auth, signal }).then(() => getInsight(playerId, { ...auth, signal })),
    ['student-insight', playerId],
  )
  const events = useResource<EventRow[]>(
    (signal) => getPlayerEvents(playerId, { ...auth, signal }),
    ['student-events', playerId],
    { isEmpty: (rows) => rows.length === 0 },
  )
  const sessions = useResource<SessionRow[]>(
    (signal) => getPlayerSessions(playerId, { ...auth, signal }),
    ['student-sessions', playerId],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const child = roster.data?.find((r) => r.playerId === playerId) ?? null
  const openClass = classes.find((c) => c.id === classId) ?? null
  const roster0 = useMemo(() => roster.data ?? [], [roster.data])

  const refetchAll = useCallback(() => {
    progress.retry()
    sessions.retry()
    roster.retry()
    onChanged?.()
  }, [progress, sessions, roster, onChanged])

  return (
    <section className="font-dash text-dash-sm text-ink max-w-5xl mx-auto pb-12">
      {/* ── Student Hero Header Card ── */}
      <div className="rounded-lg border border-line bg-surface p-3 sm:p-4 shadow-card mb-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {/* Initials, never a guess at the child gender. */}
            <Avatar name={child?.name ?? ''} size={64} />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-dash text-2xl font-extrabold text-ink">{child?.name ?? s.dash}</h1>
                <span className="h-2.5 w-2.5 rounded-full bg-[#22c55e]" />
              </div>
              <p className="mt-1 font-dash text-dash-xs font-bold text-muted">
                {[
                  child?.rollNumber ? `${s.roll} ${child.rollNumber}` : s.noRoll,
                  openClass ? classLabel(openClass) : null,
                  openClass ? BAND_LABEL[openClass.ageBand] : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/*
              🔴 THE EDIT CONTROL SITS ON THE IDENTITY CARD, BESIDE THE FACTS IT
              CORRECTS. The two things it changes — the name in the heading above
              it and the parent block below it — are both on this card, so a
              teacher who has just read a misspelling reaches the fix without
              leaving the thing that is wrong. The register controls further down
              the page (move, remove) are about the child's PLACE, not their
              record, which is why they stay where they are.

              ⚠️ IT TOGGLES A PANEL RATHER THAN OPENING A DIALOG. §"Every data
              surface ships four states" applies to the panel's own writes, and a
              modal would put a `Note` reporting a 409 inside a layer the teacher
              must dismiss to see the row it is talking about.
            */}
            <button
              type="button"
              aria-expanded={editing}
              onClick={() => setEditing((open) => !open)}
              className="rounded-full border border-line bg-surface px-4 py-2 font-dash text-dash-xs font-bold text-ink transition-colors hover:bg-canvas shadow-sm flex items-center gap-1.5 cursor-pointer"
            >
              {s.editOpen}
            </button>
            <button
              type="button"
              onClick={onBack}
              className="rounded-full border border-line bg-surface px-4 py-2 font-dash text-dash-xs font-bold text-ink transition-colors hover:bg-canvas shadow-sm flex items-center gap-1.5"
            >
              <span>←</span> {s.back}
            </button>
          </div>
        </div>

        {/*
          ⚠️ RENDERED ONLY WHEN OPEN, AND KEYED ON THE NAME THE REGISTER HOLDS.
          `EditChildPanel` seeds its field from `name` once, as a form should; the
          key makes a successful save — which refetches the roster — restart the
          field from the saved spelling rather than leave the old one sitting in
          a box that now disagrees with the heading above it.
        */}
        {editing && child !== null && (
          <div className="mt-5 border-t border-line pt-4">
            <p className="mb-1 font-dash text-dash-xs font-bold text-muted uppercase tracking-wider">
              {s.editTitle}
            </p>
            <p className="mb-4 text-dash-xs text-muted">{s.editWhy}</p>
            <EditChildPanel
              key={child.name}
              playerId={playerId}
              name={child.name}
              hasParent={
                child.parentName !== null || child.parentPhone !== null
              }
              lang={lang}
              auth={auth}
              onChanged={refetchAll}
            />
          </div>
        )}

        {/* Parent Status Alert Box */}
        <div className="mt-5 border-t border-line pt-4">
          <p className="text-dash-xs font-bold text-muted uppercase tracking-wider mb-2">Parent</p>
          <Surface resource={roster} lang={lang}>
            {() =>
              child === null ? (
                <p className="text-muted">{t.noMatch}</p>
              ) : child.parentName === null && child.parentPhone === null ? (
                <div className="rounded-xl border border-[#fef08a] bg-[#fffbeb] p-3.5 text-dash-xs text-[#b45309] flex items-start gap-2.5 shadow-sm">
                  <AlertIcon size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">{s.noParent}.</span> {s.noParentWhy}
                  </div>
                </div>
              ) : (
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Field label={s.parentName} value={child.parentName ?? s.dash} />
                  <Field
                    label={s.parentPhone}
                    value={child.parentPhone ?? s.dash}
                    hint={child.parentPhone ? s.phoneMasked : undefined}
                  />
                </dl>
              )
            }
          </Surface>
        </div>
      </div>

      {/* ── Progress Section ── */}
      <div className="mb-8">
        <h2 className="font-dash text-dash-md font-extrabold text-ink mb-3">{s.progressTitle}</h2>
        <Surface resource={progress} lang={lang}>
          {(p) => (
            <>
              {/*
                🔴 THE SAME TILE THE LEADERBOARD OPENS WITH, NOW LITERALLY THE
                SAME COMPONENT. These three were `StatCard`s — a corner wash and
                an ink numeral — while the class Leaderboard drew coins, stickers
                and streak as four full gradient cards. Same three figures, same
                three words, two designs, one sidebar apart. A teacher reads
                "Coins 100 across the class" on one screen and "Coins 53" on the
                other, and the second looked like a different kind of fact.

                ⚠️ `RewardTile` RATHER THAN A FOURTH ARRANGEMENT. The kit's own
                argument, one level up: a tile drawn by hand in two places is two
                tiles that drift. The Leaderboard's four now come from here too,
                so they cannot.

                ⚠️ AND THE TONES ARE THE ONES THE CHILD ALREADY SEES — gold for
                coins, pink for stickers, green for the streak. That is identity,
                not status (§12): the colour is fixed per metric at the call
                site, never chosen from the value.
              */}
              <StatGrid>
                <RewardTile
                  label={s.coins}
                  value={p.coins}
                  /* ⚠️ THE "last played" LINE KEEPS ITS PLACE AS THE CAPTION —
                     it is the one fact on this row that dates the other two. */
                  caption={
                    p.lastPlayed
                      ? `${s.lastPlayed}: ${new Date(p.lastPlayed).toLocaleString()}`
                      : undefined
                  }
                  icon={<PointIcon size={18} />}
                  tone="sunshine"
                />
                <RewardTile
                  label={s.stickers}
                  value={p.stickers.length}
                  icon={<StickerIcon size={18} />}
                  tone="primary"
                />
                <RewardTile
                  label={s.streak}
                  value={p.streakDays}
                  caption={p.streakDays > 0 ? s.streakCaption : undefined}
                  icon={<TrendUpIcon size={18} />}
                  tone="leaf"
                />
              </StatGrid>

              {/* ── Skills Section ── */}
              <div className="mt-8">
                <h2 className="font-dash text-dash-md font-extrabold text-ink mb-3">{s.skillsTitle}</h2>
                <div className="rounded-lg border border-line bg-surface p-3 sm:p-4 shadow-card">
                  {/*
                    🔴 THE BARS §"Progress bars" HAS ALWAYS SPECIFIED, FINALLY
                    DRAWN. The spec reads *"Content skills render as five
                    segments (`Level 3 of 5`); motor skills render as a single
                    filled bar"* — this screen rendered both as right-aligned
                    text, so the one page a teacher opens to answer "where is
                    this child" made her read eight numbers rather than see a
                    shape. The figures stay exactly where they were: the meter is
                    the second carrier, never the only one (§12).
                  */}
                  <h3 className="font-dash text-dash-sm font-extrabold text-ink mb-3">{s.contentSkills}</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {p.skills.content.map((c) => (
                      <li key={c.skill} className="flex flex-col gap-1.5">
                        <span className="flex items-center justify-between gap-4">
                          <span className="font-dash text-dash-sm font-medium text-ink">
                            {SKILL_LABEL[c.skill]?.[lang] ?? c.skill}
                          </span>
                          <span className="font-dash text-dash-sm font-extrabold text-ink tabular-nums">
                            {c.levelCompleted > 0 ? s.level(c.levelCompleted) : s.notStartedSkill}
                          </span>
                        </span>
                        <LevelMeter skill={c.skill} completed={c.levelCompleted} of={LEVEL_COUNT} />
                      </li>
                    ))}
                  </ul>

                  <div className="my-5 border-t border-line" />

                  <h3 className="font-dash text-dash-sm font-extrabold text-ink mb-3">{s.motorSkills}</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {p.skills.motor.map((m) => (
                      <li key={m.skill} className="flex flex-col gap-1.5">
                        <span className="flex items-center justify-between gap-4">
                          <span className="font-dash text-dash-sm font-medium text-ink">
                            {SKILL_LABEL[m.skill]?.[lang] ?? m.skill}
                          </span>
                          <span className="font-dash text-dash-xs font-bold text-muted tabular-nums">
                            {s.activities(m.events)}
                          </span>
                        </span>
                        {/* ⚠️ SCALED ACROSS THE SET, NOT PER ROW — `max` is the
                            busiest motor skill, so the three bars are comparable
                            to each other, which is the only true reading a count
                            with no denominator supports. */}
                        <ActivityMeter
                          skill={m.skill}
                          events={m.events}
                          max={Math.max(...p.skills.motor.map((x) => x.events), 0)}
                        />
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 rounded-xl border border-[#fef08a] bg-[#fffbeb] p-3 text-dash-xs text-[#b45309] flex items-center gap-2">
                    <InfoIcon size={16} className="shrink-0" />
                    <span>{s.motorHint}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </Surface>
      </div>

      {/* ── 2 Columns Grid: Insight & Attendance ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Insight */}
        <div className="rounded-lg border border-line bg-surface p-3 sm:p-4 shadow-card flex flex-col justify-between">
          <div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink flex items-center gap-2 mb-3">
              <SparkleIcon size={17} /> {s.insightTitle}
            </h2>
            <Surface resource={insight} lang={lang} skeletonRows={3}>
              {(i) => (
                <div className="space-y-2 font-dash text-dash-xs text-ink font-medium">
                  <p className="text-muted">{i.summary}</p>
                  {i.strengths.length > 0 && (
                    <p>
                      <span className="font-bold text-ink">{s.strengths}:</span>{' '}
                      {i.strengths.map((k) => SKILL_LABEL[k]?.[lang] ?? k).join(', ')}
                    </p>
                  )}
                  {i.focusAreas.length > 0 && (
                    <p>
                      <span className="font-bold text-ink">{s.focus}:</span>{' '}
                      {i.focusAreas.map((k) => SKILL_LABEL[k]?.[lang] ?? k).join(', ')}
                    </p>
                  )}
                </div>
              )}
            </Surface>
          </div>
        </div>

        {/* Attendance */}
        <div className="rounded-lg border border-line bg-surface p-3 sm:p-4 shadow-card">
          <h2 className="font-dash text-dash-md font-extrabold text-ink flex items-center gap-2 mb-3">
            <CalendarIcon size={17} /> {s.attendanceTitle}
          </h2>
          <AbsencePanel playerId={playerId} lang={lang} auth={auth} onChanged={refetchAll} />
        </div>
      </div>

      {/* ── 2 Columns Grid: Age band & Settings (Sessions) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Age Band */}
        <div className="rounded-lg border border-line bg-surface p-3 sm:p-4 shadow-card">
          <h2 className="font-dash text-dash-md font-extrabold text-ink flex items-center gap-2 mb-3">
            <ChildrenIcon size={17} /> {s.bandTitle}
          </h2>
          <BandPanel
            playerId={playerId}
            current={openClass?.ageBand ?? 'BAND_4_5'}
            lang={lang}
            auth={auth}
            onChanged={refetchAll}
          />
        </div>

        {/* Settings / Sessions */}
        <div className="rounded-lg border border-line bg-surface p-3 sm:p-4 shadow-card">
          <h2 className="font-dash text-dash-md font-extrabold text-ink flex items-center gap-2 mb-3">
            <RegisterIcon size={17} /> {s.sessionsTitle}
          </h2>
          <p className="text-dash-xs text-muted mb-4">{s.sessionsWhy}</p>
          <Surface resource={sessions} lang={lang} emptyMessage={s.neverPlayed} skeletonRows={3}>
            {(rows) => (
              <SessionList
                rows={rows}
                roster={roster0}
                playerId={playerId}
                lang={lang}
                auth={auth}
                onChanged={refetchAll}
              />
            )}
          </Surface>
        </div>
      </div>

      {/*
        ── Strong and stuck ──
        🔴 ABOVE THE ACTIVITY TABLE AND BELOW THE CONTROLS, WHICH IS WHERE A
        TEACHER LOOKS SECOND. The tables above say what happened; this says what
        to do about it, and the raw event list below is the evidence for both.
      */}
      <div className="mb-8">
        <StrengthsPanel playerId={playerId} lang={lang} auth={auth} />
      </div>

      {/*
        ── What this child has played ──
        🔴 BETWEEN THE ANALYSIS AND THE RAW EVENTS, WHICH IS THE ORDER A TEACHER
        DRILLS IN. `StrengthsPanel` above says *"stuck on matching"*; this says
        which game that is, how many rounds it took and what level they reached;
        the event list below is the individual answers. Each one is the evidence
        for the one above it.

        ⚠️ NO CHILD COUNT — a per-child panel showing "1 of 1" is noise, and the
        panel hides the column when the denominator is absent.
      */}
      <div className="mb-8">
        <ChildGamesPanel playerId={playerId} lang={lang} auth={auth} />
      </div>

      {/*
        ── The register ──
        ⚠️ LAST, AND IN A ROW OF ITS OWN. Everything above reads; these two
        write, and one of them takes the child off the screen the teacher is
        looking at. Putting them beside the band control would make a mis-tap
        while adjusting a level into a child leaving the roster.
      */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <MoveClassPanel
          playerId={playerId}
          classId={classId}
          classes={classes}
          lang={lang}
          auth={auth}
          onChanged={onChanged}
        />
        <RemoveFromClassPanel
          playerId={playerId}
          lang={lang}
          auth={auth}
          /* 🔴 IT GOES BACK TO THE ROSTER RATHER THAN REFETCHING. The child is no
             longer in this class, so every panel on this page would reload into
             an empty or refused state — the honest next screen is the register
             they were just taken off. */
          onChanged={() => {
            onChanged?.()
            onBack?.()
          }}
        />
      </div>

      {/* ── Recent Activity Section ── */}
      <div className="mb-8">
        <h2 className="font-dash text-dash-md font-extrabold text-ink mb-3">{s.activityTitle}</h2>
        <Surface resource={events} lang={lang} emptyMessage={s.neverPlayed} skeletonRows={5}>
          {(rows) => <ActivityTable rows={rows} lang={lang} s={s} />}
        </Surface>
      </div>

      {/* ── Privacy Disclaimer Banner ── */}
      <div className="rounded-2xl border border-[#fef08a] bg-[#fffbeb] p-4 text-dash-xs text-[#b45309] flex items-center gap-3 shadow-sm">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fef3c7] text-[#d97706]">
          <PrivacyIcon size={20} />
        </div>
      </div>
    </section>
  )
}

function ActivityTable({
  rows,
  lang,
  s,
}: {
  rows: readonly EventRow[]
  lang: Lang
  s: (typeof STUDENT_COPY)[Lang]
}) {
  const [showAll, setShowAll] = useState(false)
  const visibleRows = showAll ? rows : rows.slice(0, 10)
  const hasMore = rows.length > 10

  return (
    <div className="rounded-lg border border-line bg-surface overflow-hidden shadow-card">
      <div className="scroll-x" tabIndex={0}>
      <table className="w-full border-collapse text-left font-dash text-dash-xs">
        <thead>
          <tr className="bg-[#fdf2f8] border-b border-line text-ink font-extrabold">
            <th className="px-4 py-3">{s.when}</th>
            <th className="px-4 py-3">{s.game}</th>
            <th className="px-4 py-3">{s.what}</th>
            <th className="px-4 py-3">{s.skill}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {visibleRows.map((e) => (
            <tr key={e.id} className="hover:bg-canvas transition-colors">
              <td className="px-4 py-3 text-muted">{new Date(e.createdAt).toLocaleString()}</td>
              <td className="px-4 py-3 font-bold text-ink flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#f59e0b]" />
                {e.game}
              </td>
              <td className="px-4 py-3 font-bold text-ink">{e.type}</td>
              <td className="px-4 py-3 text-muted">
                {e.skill ? (SKILL_LABEL[e.skill]?.[lang] ?? e.skill) : s.dash}
                {e.level ? ` · ${s.level(e.level)}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {hasMore && (
        <div className="p-3 text-center border-t border-line">
          <button
            type="button"
            onClick={() => setShowAll((prev) => !prev)}
            className="font-dash text-dash-xs font-bold text-primary hover:underline cursor-pointer"
          >
            {showAll ? 'Show less ∧' : `View all activity (${rows.length}) ∨`}
          </button>
        </div>
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────────── helpers ── */

function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  skeletonRows = 4,
}: {
  resource: Resource<T>
  lang: Lang
  children: (data: T) => React.ReactNode
  emptyMessage?: React.ReactNode
  skeletonRows?: number
}) {
  const t = TEACHER_COPY[lang]
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.emptyClass}
      errorMessage={describeError(resource.error, lang)}
      errorLastWorked={
        resource.lastLoadedAt
          ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
          : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  )
}

function Field({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <dt className="text-dash-xs font-bold text-muted">{label}</dt>
      <dd className="mt-0.5 text-dash-sm text-ink">{value}</dd>
      {hint ? <dd className="mt-0.5 text-dash-xs text-muted">{hint}</dd> : null}
    </div>
  )
}

/** What a failed write is called. 403 is its own answer — see `classifyWriteError`. */
function failureText(err: unknown, lang: Lang): string {
  const s = STUDENT_COPY[lang]
  const status = (err as { status?: number } | null)?.status
  return status === 401 || status === 403 ? s.refused : s.failed
}

/* ───────────────────────────────────────────────────────── T7 ── */

/**
 * 🔴 T7, AND THE LOGIC BEHIND IT HAS BEEN UNREACHABLE ALL ALONG.
 *
 * `attention.ts` already discounts absent days from the idle count — *"a child
 * who was away on Monday and Tuesday has not been idle for two days in any
 * sense a teacher cares about"* — and **nothing in the product could write an
 * absence**. So the discount had never once applied, and every absent child was
 * reported to their teacher as needing chasing.
 */
function AbsencePanel({
  playerId,
  lang,
  auth,
  onChanged,
}: {
  playerId: string
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
}) {
  const s = STUDENT_COPY[lang]
  const [state, setState] = useState<'idle' | 'saving' | 'marked' | 'failed'>('idle')
  const [error, setError] = useState<unknown>(null)

  const run = async (mark: boolean) => {
    setState('saving')
    setError(null)
    try {
      if (mark) await markAbsentToday(playerId, auth)
      else await unmarkAbsentToday(playerId, auth)
      setState(mark ? 'marked' : 'idle')
      onChanged()
    } catch (err) {
      setError(err)
      setState('failed')
    }
  }

  return (
    <Panel>
      <div className="flex flex-wrap items-center gap-3">
        <DashButton
          variant={state === 'marked' ? 'secondary' : 'neutral'}
          disabled={state === 'saving'}
          onClick={() => void run(state !== 'marked')}
        >
          {state === 'saving' ? s.saving : state === 'marked' ? s.unmarkAbsent : s.markAbsent}
        </DashButton>
        {state === 'marked' && (
          <StatusPill tone="neutral" icon={<CheckIcon size={14} />}>
            {s.absentToday}
          </StatusPill>
        )}
      </div>
      {/* Says what it actually changes — and what it does NOT touch. */}
      <p className="mt-3 max-w-prose text-dash-xs text-muted">{s.absentWhy}</p>
      {state === 'failed' && (
        <p className="mt-3">
          <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
            {failureText(error, lang)}
          </StatusPill>
        </p>
      )}
    </Panel>
  )
}

/* ───────────────────────────────────────────────────────── T4 ── */

const BANDS: AgeBand[] = ['BAND_3_4', 'BAND_4_5', 'BAND_5_6']

function BandPanel({
  playerId,
  current,
  lang,
  auth,
  onChanged,
}: {
  playerId: string
  current: AgeBand
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
}) {
  const s = STUDENT_COPY[lang]
  const [band, setBand] = useState<AgeBand>(current)
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const [error, setError] = useState<unknown>(null)

  const save = async () => {
    setState('saving')
    setError(null)
    try {
      await setPlayerBand(playerId, band, auth)
      setState('saved')
      onChanged()
    } catch (err) {
      setError(err)
      setState('failed')
    }
  }

  return (
    <Panel>
      <FormField label={s.bandTitle}>
        <Select value={band} onChange={(e) => setBand(e.target.value as AgeBand)}>
          {BANDS.map((b) => (
            <option key={b} value={b}>
              {BAND_LABEL[b]}
            </option>
          ))}
        </Select>
      </FormField>
      {/* 🔴 "A default, not a diagnosis" — org.prisma's own words. The copy must
          not imply an assessment has been recorded about this child. */}
      <p className="mt-3 max-w-prose text-dash-xs text-muted">{s.bandWhy}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <DashButton variant="secondary" disabled={state === 'saving'} onClick={() => void save()}>
          {state === 'saving' ? s.saving : s.bandSave}
        </DashButton>
        {state === 'saved' && (
          <StatusPill tone="success" icon={<CheckIcon size={14} />}>
            {s.bandSaved}
          </StatusPill>
        )}
        {state === 'failed' && (
          <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
            {failureText(error, lang)}
          </StatusPill>
        )}
      </div>
    </Panel>
  )
}

/* ───────────────────────────────────────────────────────── T1 ── */

function SessionList({
  rows,
  roster,
  playerId,
  lang,
  auth,
  onChanged,
}: {
  rows: readonly SessionRow[]
  roster: readonly RosterRow[]
  playerId: string
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
}) {
  const s = STUDENT_COPY[lang]
  const [open, setOpen] = useState<string | null>(null)

  return (
    /*
     * 🔴 THE SITTINGS SCROLL INSIDE THE PANEL NOW; THEY USED TO STRETCH IT.
     *
     * There was no bound on this table at all, and a sitting is a row per time
     * the child played — a term's worth is dozens. The panel grew to hold every
     * one of them, and because it shares a grid ROW with "Age band" beside it,
     * that height went to the age-band card too: a card holding one select and
     * one button was drawn a thousand pixels tall, with the control stranded at
     * the top and nothing under it. Everything below the row — "Strong and
     * stuck", the activity table, the privacy notice — was pushed off the
     * screen behind a scroll nobody wanted.
     *
     * ⚠️ `dvh`, NOT A ROW COUNT. The bound has to be "never taller than the
     * screen it is read on" rather than "about eight sittings", which is a
     * different number of pixels on every window; a taller display simply shows
     * more of the child's history.
     *
     * ⚠️ THE HEADER IS STICKY, WHICH IS THE HALF THAT MAKES IT USABLE. Scrolling
     * a bare table takes "Started · Length · Activities" off the top, and three
     * columns of dates and integers with no headings is a table you have to
     * scroll back up to read. `bg-canvas` on `Thead` is already opaque, so the
     * rows pass underneath it rather than through it.
     *
     * ⚠️ `overscroll-contain` STOPS THE HAND-OFF at the last sitting, so the
     * flick that reaches the bottom of the list does not carry on and take the
     * whole page with it.
     */
    <TableCard>
      <div className="max-h-[55dvh] overflow-y-auto overscroll-contain">
        <table className="w-full border-collapse">
          <Thead sticky headers={[s.started, s.duration, s.activityCount, '']} />
        <tbody>
          {rows.map((row) => (
            <Tr key={row.id}>
              <Td>{new Date(row.startedAt).toLocaleString()}</Td>
              <Td soft>
                {row.endedAt === null
                  ? s.running
                  : `${String(
                      Math.max(
                        1,
                        Math.round(
                          (new Date(row.endedAt).getTime() - new Date(row.startedAt).getTime()) /
                            60_000,
                        ),
                      ),
                    )} min`}
              </Td>
              <Td>
                {/* 🔴 "Nothing kept" IS NOT "did nothing" — ADR-030. A session
                    with `recorded: false` never wrote an event, and collapsing
                    the two would tell a teacher a child sat idle. */}
                {row.recorded ? (
                  <span className="tabular-nums">{row.events}</span>
                ) : (
                  <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
                    {s.notRecorded}
                  </StatusPill>
                )}
              </Td>
              <Td>
                <DashButton
                  variant="ghost"
                  onClick={() => setOpen(open === row.id ? null : row.id)}
                >
                  {s.reassign}
                </DashButton>
                {open === row.id && (
                  <ReassignForm
                    sessionId={row.id}
                    roster={roster}
                    playerId={playerId}
                    lang={lang}
                    auth={auth}
                    onDone={() => {
                      setOpen(null)
                      onChanged()
                    }}
                  />
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
        </table>
      </div>
    </TableCard>
  )
}

function ReassignForm({
  sessionId,
  roster,
  playerId,
  lang,
  auth,
  onDone,
}: {
  sessionId: string
  roster: readonly RosterRow[]
  playerId: string
  lang: Lang
  auth?: StaffAuth
  onDone: () => void
}) {
  const s = STUDENT_COPY[lang]
  const [to, setTo] = useState('')
  const [state, setState] = useState<'idle' | 'saving' | 'done' | 'failed'>('idle')
  const [moved, setMoved] = useState(0)
  const [error, setError] = useState<unknown>(null)

  const run = async () => {
    if (to === '') return
    setState('saving')
    setError(null)
    try {
      const result = await reassignSession(sessionId, to, auth)
      setMoved(result.eventsMoved)
      setState('done')
      onDone()
    } catch (err) {
      setError(err)
      setState('failed')
    }
  }

  return (
    <div className="mt-4 max-w-sm rounded-lg border border-line bg-canvas p-4">
      <p className="text-dash-sm font-extrabold text-ink">{s.reassignTitle}</p>
      <div className="mt-3">
        <FormField label={s.reassignPick} width="full">
          <Select value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">{s.dash}</option>
            {/* Never the child whose page this is — moving a sitting to its own
                owner is the no-op D4 forbids offering. */}
            {roster
              .filter((r) => r.playerId !== playerId)
              .map((r) => (
                <option key={r.playerId} value={r.playerId}>
                  {r.rollNumber ? `${r.rollNumber} · ` : ''}
                  {r.name}
                </option>
              ))}
          </Select>
        </FormField>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <DashButton
          variant="secondary"
          disabled={state === 'saving' || to === ''}
          onClick={() => void run()}
        >
          {state === 'saving' ? s.saving : s.reassignDo}
        </DashButton>
      </div>

      {/* 🔴 THE HALF IT CANNOT FIX, BEFORE THE CLICK AND AFTER IT. Coins,
          stickers and streaks are device-authored with no per-session ledger,
          so the server cannot move what a sitting was worth. */}
      <p className="mt-2 text-dash-xs text-muted">{s.reassignPartial}</p>

      {state === 'done' && (
        <p className="mt-2">
          <StatusPill tone="success" icon={<CheckIcon size={14} />}>
            {s.reassignDone(moved)}
          </StatusPill>
        </p>
      )}
      {state === 'failed' && (
        <p className="mt-2">
          <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
            {failureText(error, lang)}
          </StatusPill>
        </p>
      )}
    </div>
  )
}

/**
 * One child's games.
 *
 * 🔴 THE SAME PANEL THE CLASS AND SCHOOL TIERS DRAW, OVER A SET OF ONE, AND THE
 * SAME SERVER COMPUTATION BEHIND IT. A per-child table written separately would
 * eventually disagree with the class panel that contains it — and the
 * disagreement would surface as a teacher and a parent reading two different
 * numbers about the same child, which is the failure `architecture.md` §12 exists
 * to prevent.
 *
 * ⚠️ `childCount` IS DELIBERATELY OMITTED. On a one-child panel the "children"
 * column would read "1 of 1" on every row, which is noise; the panel drops the
 * column rather than rendering a denominator nobody needs.
 */
function ChildGamesPanel({
  playerId,
  lang,
  auth,
}: {
  playerId: string
  lang: Lang
  auth?: StaffAuth
}) {
  const t = TEACHER_COPY[lang]
  const resource = useResource<GameStatsResponse>(
    (signal) => getPlayerGames(playerId, { ...auth, signal }),
    ['child-games', playerId],
  )

  return (
    <DataSurface
      state={resource.state}
      skeletonRows={5}
      errorMessage={describeError(resource.error, lang)}
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : (
        <GamesPanel
          games={resource.data.games}
          summary={resource.data.summary}
          lang={lang}
          copy={{
            ...GAMES_PANEL_COPY[lang],
            title: t.childGamesTitle,
            hint: t.childGamesHint,
          }}
        />
      )}
    </DataSurface>
  )
}
